import * as _ from 'underscore'

import { DataProvider, LogHelper, ConvertHelper, ToastHelper } from "../helpers"

import { OpTask, OpFile, OpTaskStatus } from "../models/core"
import { Enums } from "../models/common"
import { ColumnInfo, OpTaskStatusFlow } from "../models/custom"

import { OpTaskPaymentElement } from "../models/custom/"
import { DataProviderRequest } from './utils'
import { OpTaskProtocolVerification } from '../models/custom/op-task-protocol-verification'

export class DataProviderTask {

    // #region Consts

    private static get LOG_PREFIX(): string { return '[SIMAX.DataProvider.Task]' }

    // #endregion Consts End


    // #region Ctor

    constructor(private dataProvider: DataProvider) { }

    // #endregion Ctor End

    // #region Life Cycle

    public changeTaskStatus = (ids: number[], idTaskStatus: number): JQueryPromise<OpTask[]> => {
        return this.dataProvider
            .sendRequestWithSession('OpxChangeTaskStatus', { IdTask: ids, IdTaskStatus: idTaskStatus }, 'OpxChangeTaskStatusResult')
            .then((response: any[]) => {
                if (response != null) {
                    return response.map((obj: any) => new OpTask(obj))
                }
                return null
            })
    }

    public changeTaskUsedEquipment = (id: number, equipment: any[]): JQueryPromise<OpTask> => {
        return this.dataProvider
            .sendRequestWithSession('OpxChangeTaskUsedEquipment', { idTask: id, equipment: equipment }, 'OpxChangeTaskUsedEquipmentResult')
            .then((response: any) => {
                const task: OpTask = new OpTask(response)

                return OpTask.loadNavigationProperties(this.dataProvider, [task])
                    .then((tasks: OpTask[]) => {
                        return _.first(tasks)
                    })

            })
    }

    // #endregion

    // #region Delete

    public deleteTaskAttachment = (id: number, idFile: number[]): JQueryPromise<void> => {
        return this.dataProvider.sendRequestWithSession('OpxDeleteTaskAttachment', { idTask: id, IdFile: idFile }, null)
    }

    public deleteTask = (task: OpTask): JQueryPromise<OpTask> => {
        return this.dataProvider
            .sendRequestWithSession('OpxDeleteTask', { IdTask: task.idTask }, 'OpxDeleteTaskResult')
            .then((response: any) => {

                const task: OpTask = new OpTask(response)

                return OpTask.loadNavigationProperties(this.dataProvider, [task])
                    .then((tasks: OpTask[]) => {
                        return _.first(tasks)
                    })

            })
    }

    // #endregion

    // #region Get

    public getTask = (id: number): DataProviderRequest<OpTask> => {
        const promise: JQueryPromise<OpTask> = this.dataProvider
            .sendRequestWithSession('OpxGetTask', { IdTask: id }, 'OpxGetTaskResult')
            .then((response: any) => {
                const task: OpTask = new OpTask(response)

                return OpTask.loadNavigationProperties(this.dataProvider, [task])
                    .then((tasks: OpTask[]) => {
                        return _.first(tasks)
                    })

            })
            .fail((error) => {
                const msg: string = ConvertHelper.getRequestErrorMessage(error)
                ToastHelper.showError(msg)
            })

        return DataProviderRequest.fromJQuery(promise)
    }

    public getTaskFilter = (columns: ColumnInfo[], pageNbr: number, pageSize: number, customWhereClause: string) => {
        return this.dataProvider.sendFilterRequestWithSession<OpTask>(columns, pageNbr, pageSize, customWhereClause, Enums.ReferenceType.IdTask, 'OpxTaskFilter', 'OpxTaskFilterResult')
    }

    public getTaskAttachment = (id: number): DataProviderRequest<OpFile[]> => {
        const promise: JQueryPromise<OpFile[]> = this.dataProvider
            .sendRequestWithSession('OpxGetTaskAttachment', { IdTask: id, IdFile: null }, 'OpxGetTaskAttachmentResult')
            .then((response: any[]) => {
                return response.map((opObj: any) => new OpFile(opObj))
            })

        return DataProviderRequest.fromJQuery(promise)
    }

    public getTaskAttachmentPacked = (id: number, filesId: number[]): DataProviderRequest<OpFile> => {
        const promise: JQueryPromise<OpFile> = this.dataProvider
            .sendRequestWithSession('OpxGetTaskAttachmentPacked', { idTask: id, idFile: filesId }, 'OpxGetTaskAttachmentPackedResult')
            .then((response: any) => {
                return new OpFile(response)
            })

        return DataProviderRequest.fromJQuery(promise)
    }

    public getNextTaskStatuses = (taskStatus: Enums.TaskStatus | Enums.TaskStatus[]): JQueryPromise<OpTaskStatus[]> => {
        if (_.isArray(taskStatus) && taskStatus.length > 1) {
            return this.dataProvider
                .sendRequestWithSession('OpxGetTaskStatusChangeList', { IdTaskStatus: taskStatus }, 'OpxGetTaskStatusChangeListResult')
                .then((response: any[]) => OpTaskStatus.loadNavigationProperties(this.dataProvider, DataProvider.convertArrayResult(response, OpTaskStatus)))
                .fail((error: any) => {
                    LogHelper.warn(DataProviderTask.LOG_PREFIX, `Unable to load next flow for task statuses: ${taskStatus}. Returning an empty list.`, ConvertHelper.getRequestErrorMessage(error))

                    return []
                })
        }

        if (_.isArray(taskStatus) && taskStatus.length === 1) {
            taskStatus = taskStatus[0]
        }

        return this.dataProvider.System.getFlowByStatus<OpTaskStatusFlow, OpTaskStatus>(Enums.DataType.HELPER_ID_TASK_STATUS, (status: OpTaskStatusFlow) => status.taskStatus.idTaskStatus === taskStatus)
    }

    public getTaskPayments = (id: number, savedPayments: boolean): JQueryPromise<OpTaskPaymentElement[]> => {
        return this.dataProvider
            .sendRequestWithSession('OpxGetTaskPayments', { IdTask: id, SavedPayments: savedPayments, DefaultPayments: true }, 'OpxGetTaskPaymentsResult')
            .then((response: any[]) => {
                return response.map((obj: any) => new OpTaskPaymentElement(obj))
            })
    }

    public getTaskStatusFlow = (): JQueryPromise<OpTaskStatusFlow[]> => {
        return this.dataProvider.System.getFlow<OpTaskStatusFlow>(Enums.DataType.HELPER_ID_TASK_STATUS)
    }

    public getTaskProtocolToVerification = (id: number): JQueryPromise<OpTaskProtocolVerification[]> => {
        return this.dataProvider
            .sendRequestWithSession('OpxGetTaskProtocolToVerification', { idTask: id }, 'OpxGetTaskProtocolToVerificationResult')
            .then((response: any[]) => {
                if (_.size(response) > 0) {
                    return _.map(response, (item: any) => new OpTaskProtocolVerification(item))
                }

                return []
            })
    }

    // #endregion

    // #region Save

    public saveTask = (opObj: OpTask): JQueryPromise<OpTask> => {
        return this.dataProvider
            .sendRequestWithSession('OpxSaveTask', { ToBeSaved: opObj.toJSON() }, 'OpxSaveTaskResult')
            .then((response: any) => {
                const task: OpTask = new OpTask(response)

                return OpTask.loadNavigationProperties(this.dataProvider, [task])
                    .then((tasks: OpTask[]) => {
                        return _.first(tasks)
                    })

            })
    }

    public saveTaskAttachment = (id: number, file: OpFile): JQueryPromise<OpFile> => {
        return this.dataProvider
            .sendRequestWithSession('OpxSaveTaskAttachment', { IdTask: id, file: file.toJSON() }, 'OpxSaveTaskAttachmentResult')
            .then((response: any) => new OpFile(response))
    }

    // #endregion
}